/**
 * Calendar-only meetings — TASK-669.
 *
 * Events booked in Outlook that have no Kielis session yet. They are shown as distinct
 * rows and MATERIALIZED (a session created) only when the RM explicitly acts on one — no
 * background sync, so no phantom sessions for dentist appointments or stand-ups.
 */
import { httpClient } from '../../lib/httpClient';

/** Why an event is offered, or what it still needs. */
export type CalendarEventClass = 'candidate' | 'needs_client';

/**
 * The ONLY fields the server discloses. Private event content (bodyPreview, attendees,
 * organizer) never crosses the wire — see services/calendar_classification.py.
 */
export interface CalendarEvent {
  event_id: string;
  subject: string;
  start: string | null;
  end: string | null;
  classification: CalendarEventClass;
  reason?: string | null;
  client_id?: string | null;
  client_name?: string | null;
  has_conference_url: boolean;
  is_recurring_occurrence: boolean;
}

/**
 * Distinct degradation states — each has a different fix, so the UI must not collapse them
 * into one "calendar unavailable".
 */
export type CalendarStatus =
  | 'ok'
  | 'empty'
  | 'no_mailbox'
  | 'not_configured'
  | 'no_grant'
  | 'auth_expired'
  | 'provider_error';

export interface CalendarWindow {
  status: CalendarStatus;
  events: CalendarEvent[];
  message: string | null;
}

export async function getCalendarWindow(): Promise<CalendarWindow> {
  return httpClient.get<CalendarWindow>('/meeting/calendar/window');
}

/** Idempotent server-side by a unique index on (account_id, calendar_event_id). */
export async function materializeCalendarEvent(input: {
  calendar_event_id: string;
  client_id: string;
  subject?: string;
  scheduled_at?: string | null;
}): Promise<{ session_id: string; created: boolean }> {
  return httpClient.post('/meeting/calendar/materialize', input);
}
